// The customer record page — route /customers/:id.
//
// THIS SCREEN EXISTS FOR ONE SENTENCE. CustomerContext.jsx's own header says
// "there is no per-customer ticket filter in v1, so a caller with no organization
// gets their own details and nothing more" — because GET /api/tickets filters on
// `organization_id` and on nothing narrower. GET /api/customers/:id/tickets is what
// closes that, and the Tickets section below is the whole reason to open this page:
// with a caller on the line, what is already open for them.
//
// WHAT IS EDITABLE, AND WHAT IS STILL NOT. Name, phone, position and the account
// (organization and store) are editable through EditPanel; roles are recorded through
// RolesPanel. Moving the account is confirmed first, because `organization.brand` is a
// ROUTING dimension — 19 trigger conditions and 82 automation conditions key on it —
// so the move silently reroutes this contact's future tickets and changes a brand
// nobody chose. What remains read-only is not tidiness; each one is a thing this
// schema cannot support yet (design §6):
//   * `customers` has no `is_active` column and every FK into it is NO_ACTION, so a
//     contact with history cannot be deactivated OR deleted. There is no "remove a
//     contact" operation to expose.
//   * Email is the unique natural key under a case-insensitive collation; editing
//     it is a merge wearing a disguise, and merge rewrites `tickets.customer_id`,
//     `ticket_ccs` and inbound message attribution across four tables.
//   * There are no customer tags and no customer notes: neither column exists.
//     `organizations.notes` does, which is what "Account notes" below renders.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';

import { api } from '../../api/client.js';
import { StatusBadge, PriorityLabel, ChannelLabel } from '../../components/tickets/Badges.jsx';
import { formatDateTime, formatRelative } from '../../components/tickets/format.js';
import { PAGE_SIZE, customerIdentity } from './customer-query.js';
import EditPanel from './EditPanel.jsx';
import RolesPanel from './RolesPanel.jsx';
import { supportPath } from '../../support-path.js';

const TICKET_COLUMNS = [
  { key: 'ref', label: 'Ref', width: 'w-[5rem]' },
  { key: 'subject', label: 'Subject', width: 'w-[40%]' },
  { key: 'status', label: 'Status', width: 'w-[8rem]' },
  { key: 'priority', label: 'Priority', width: 'w-[5.5rem]' },
  { key: 'channel', label: 'Channel', width: 'w-[5.5rem]' },
  { key: 'created', label: 'Created', width: 'w-[7rem]' },
];

/**
 * One `<dl>` row, or nothing.
 *
 * Returning null on a falsy value is the same rule CustomerContext.jsx uses, and
 * post-018 it is what keeps this page honest: a legitimate contact can be a name
 * and a phone number and nothing else, and a column of em dashes reads as a broken
 * record rather than as an ordinary cashier.
 */
function Row({ label, value, children }) {
  if (!value && !children) return null;
  return (
    <div className="flex gap-3">
      <dt className="w-32 shrink-0 text-ink-secondary">{label}</dt>
      <dd className="min-w-0 flex-1 text-ink">{children ?? value}</dd>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <h2 className="text-sm font-semibold text-ink">{title}</h2>
      {children}
    </section>
  );
}

function ErrorCard({ title, message, onRetry }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
      <p className="text-sm font-medium text-red-800 dark:text-red-200">{title}</p>
      <p className="mt-1 text-sm text-red-700 dark:text-red-300">{message}</p>
      {onRetry && (
        <button
          type="button"
          className="btn-ghost mt-3 ring-1 ring-red-300 dark:ring-red-800"
          onClick={onRetry}
        >
          Try again
        </button>
      )}
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();

  const [record, setRecord] = useState({ status: 'loading', data: null, error: null });
  const [editing, setEditing] = useState(false);
  const [tickets, setTickets] = useState({ status: 'loading', rows: [], page: null, error: null });
  const [offset, setOffset] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);

  // Two effects rather than one, each with its own AbortController and its own
  // `cancelled` flag. They page independently — stepping through the ticket list
  // must not refetch the record — and a failure in one must not blank the other.
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setRecord((previous) => ({ ...previous, status: 'loading', error: null }));

    api
      .get(`/customers/${encodeURIComponent(id)}`, { signal: controller.signal })
      .then((payload) => {
        if (!cancelled) setRecord({ status: 'ready', data: payload?.data ?? null, error: null });
      })
      .catch((error) => {
        if (cancelled || error?.name === 'AbortError') return;
        setRecord({ status: 'error', data: null, error });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [id, reloadToken]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setTickets((previous) => ({ ...previous, status: 'loading', error: null }));

    api
      .get(`/customers/${encodeURIComponent(id)}/tickets?limit=${PAGE_SIZE}&offset=${offset}`, {
        signal: controller.signal,
      })
      .then((payload) => {
        if (cancelled) return;
        setTickets({
          status: 'ready',
          rows: payload?.data ?? [],
          page: payload?.page ?? null,
          error: null,
        });
      })
      .catch((error) => {
        if (cancelled || error?.name === 'AbortError') return;
        setTickets({ status: 'error', rows: [], page: null, error });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [id, offset, reloadToken]);

  // Paging resets when the customer does — otherwise following a link from page 2
  // of one contact lands on page 2 of the next one's history.
  useEffect(() => {
    setOffset(0);
  }, [id]);

  if (record.status === 'error') {
    return (
      <div className="space-y-4">
        <ErrorCard
          title={
            record.error?.status === 404 ? 'No such contact.' : 'Could not load this contact.'
          }
          message={record.error?.message}
          onRetry={record.error?.status === 404 ? null : () => setReloadToken((t) => t + 1)}
        />
        <Link to={supportPath('/customers')} className="text-sm text-brand-700 hover:underline dark:text-brand-300">
          Back to customers
        </Link>
      </div>
    );
  }

  const customer = record.data;
  const identity = customerIdentity(customer);
  const organization = customer?.organization ?? null;
  const location = customer?.location ?? null;
  const page = tickets.page;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Link
            to={supportPath('/customers')}
            className="text-xs text-brand-700 hover:underline dark:text-brand-300"
          >
            ← Customers
          </Link>
          <h1 className="mt-1 truncate text-xl font-semibold tracking-tight">
            {record.status === 'loading' && !customer ? 'Loading…' : identity.primary}
          </h1>
          {customer?.position && (
            <p className="text-sm text-ink-secondary">{customer.position}</p>
          )}
        </div>
        {/* A new draft with this customer as the caller: `/tickets/new` turns
            `?customer=` into a one-time prefill (pages/tickets/NewDraft.jsx), and the
            create screen loads the customer's organization, store and record from it, so
            the agent does not identify the caller twice. */}
        <div className="flex shrink-0 gap-2">
          {customer && !editing && (
            <button
              type="button"
              className="btn-ghost ring-1 ring-line-strong"
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
          )}
          <Link to={supportPath(`/tickets/new?customer=${encodeURIComponent(id)}`)} className="btn-primary">
            New ticket
          </Link>
        </div>
      </div>

      {/* The editor REPLACES the read-only summary rather than sitting above it, so
          there is never a stale copy of the same fields on screen disagreeing with
          what is being typed. */}
      {customer && editing && (
        <EditPanel
          customer={customer}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            // REFETCHED, not merged. PATCH answers in the DIRECTORY shape — a flat
            // `organization_name` — while this screen reads the detail shape's nested
            // `organization.name`, so splicing the response in would blank the very
            // fields the edit had just set. One request, on an action taken rarely,
            // against a whole class of shape-mismatch bug.
            setReloadToken((t) => t + 1);
            setEditing(false);
          }}
        />
      )}

      {customer && !editing && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Section title="Contact">
            <dl className="mt-2 space-y-1 text-sm">
              <Row label="Name" value={customer.name} />
              <Row label="Position" value={customer.position} />
              <Row label="Phone" value={customer.phone} />
              <Row label="Email">
                {customer.email ? (
                  <>
                    <span className="break-all">{customer.email}</span>
                    {/* `email_bounced_at` is set by the mail worker when delivery
                        hard-failed. Saying so here is what stops an agent promising
                        a caller an email that will not arrive. */}
                    {customer.email_bounced_at && (
                      <p className="mt-1 text-xs text-ink-muted">
                        Mail to this address is switched off. It bounced{' '}
                        {formatRelative(customer.email_bounced_at)}.
                      </p>
                    )}
                  </>
                ) : null}
              </Row>
              {/* Free text from an import or a mail signature, and only shown when
                  it says something the organization row does not. */}
              {customer.company && customer.company !== organization?.name && (
                <Row label="Company" value={customer.company} />
              )}
              <Row label="First seen" value={formatDateTime(customer.created_at)} />
            </dl>
          </Section>

          <Section title="Account">
            <dl className="mt-2 space-y-1 text-sm">
              {/* Plain text, not a link: there is no organization page in v1. */}
              <Row label="Organization" value={organization?.name} />
              {/* Read-only, deliberately — see this file's header. */}
              <Row label="Brand" value={organization?.brand?.name} />
              <Row label="Store" value={location?.name} />
              <Row label="Account number" value={location?.account_number} />
              <Row label="Address" value={location?.address} />
              <Row label="Store phone" value={location?.phone} />
              <Row label="Timezone" value={location?.timezone} />
              {!organization && !location && (
                <p className="text-sm text-ink-muted">
                  This contact is not linked to an organization or a store.
                </p>
              )}
            </dl>
          </Section>

          <RolesPanel customer={customer} />

          {/* The standing account context SPEC.md §9 asks for — "calls about the
              second terminal", "owner prefers text". Rendered only when there is
              something to say, and read-only: `organizations.notes` exists,
              `customers.notes` does not. */}
          {organization?.notes && (
            <Section title="Account notes">
              <p className="mt-2 whitespace-pre-wrap text-sm text-ink-secondary">
                {organization.notes}
              </p>
            </Section>
          )}
        </div>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-ink">Tickets</h2>
          {page && (
            <p className="text-sm text-ink-muted">
              {page.total.toLocaleString()} {page.total === 1 ? 'ticket' : 'tickets'}
              {customer && customer.ticket_counts.open > 0
                ? ` · ${customer.ticket_counts.open} open`
                : ''}
            </p>
          )}
        </div>

        {tickets.status === 'error' && (
          <ErrorCard
            title="Could not load this contact's tickets."
            message={tickets.error?.message}
            onRetry={() => setReloadToken((t) => t + 1)}
          />
        )}

        <div className="overflow-x-auto rounded-lg border border-line bg-surface">
          <table className="w-full table-fixed divide-y divide-line text-sm">
            <colgroup>
              {TICKET_COLUMNS.map((col) => (
                <col key={col.key} className={col.width} />
              ))}
            </colgroup>
            <thead className="bg-sunken text-left text-xs font-medium uppercase tracking-wide text-ink-secondary">
              <tr>
                {TICKET_COLUMNS.map((col) => (
                  <th key={col.key} scope="col" className="truncate px-4 py-2.5">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {tickets.status === 'loading' && tickets.rows.length === 0 && (
                <tr>
                  <td
                    colSpan={TICKET_COLUMNS.length}
                    className="px-4 py-12 text-center text-sm text-ink-muted"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {tickets.status === 'ready' && tickets.rows.length === 0 && (
                <tr>
                  <td
                    colSpan={TICKET_COLUMNS.length}
                    className="px-4 py-12 text-center text-sm text-ink-muted"
                  >
                    No tickets for this contact yet.
                  </td>
                </tr>
              )}
              {tickets.rows.map((ticket) => (
                <tr key={ticket.id} className="group transition-colors hover:bg-sunken">
                  <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-ink-muted group-hover:text-ink-secondary">
                    {/* Every ticket URL is keyed on `public_ref`, the
                        customer-visible number, never on `tickets.id`. */}
                    <Link
                      to={supportPath(`/tickets/${ticket.public_ref}`)}
                      className="hover:text-brand-700 dark:hover:text-brand-300"
                    >
                      #{ticket.public_ref}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    <Link
                      to={supportPath(`/tickets/${ticket.public_ref}`)}
                      title={ticket.subject}
                      className="block truncate font-medium text-ink hover:text-brand-700 dark:hover:text-brand-300"
                    >
                      {ticket.subject}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">
                    <StatusBadge status={ticket.status} namedStatus={ticket.named_status} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">
                    <PriorityLabel priority={ticket.priority} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">
                    <ChannelLabel channel={ticket.channel} />
                  </td>
                  <td
                    className="whitespace-nowrap px-4 py-2 text-ink-muted group-hover:text-ink-secondary"
                    title={formatDateTime(ticket.created_at) ?? undefined}
                  >
                    {formatRelative(ticket.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {page && page.total > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-ink-muted">
              Showing {(page.offset + 1).toLocaleString()}–
              {(page.offset + tickets.rows.length).toLocaleString()} of{' '}
              {page.total.toLocaleString()}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-ghost ring-1 ring-line-strong"
                disabled={page.offset === 0}
                onClick={() => setOffset(Math.max(0, page.offset - PAGE_SIZE))}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn-ghost ring-1 ring-line-strong"
                disabled={!page.has_more}
                onClick={() => setOffset(page.offset + PAGE_SIZE)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
